import { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { AddIcon, CheckmarkIcon, CloseIcon, DateIcon, SpinnerIcon } from "@/lib/icons";
import { formatDateFi } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  filterMemoTasks,
  MEMO_DEADLINE_CHIPS,
  type MemoDeadlineChip,
  type MemoTask,
} from "@/lib/memoList";
import { useMemoTaskMutations, useMemoTasks } from "@/hooks/useMemoLists";
import { MemoDeadlinePopover, MemoTaskRow } from "@/components/memo/MemoTaskRow";

function EditableMemoTaskRow({
  task,
  onSave,
  onCancel,
  saving,
}: {
  task: MemoTask;
  onSave: (title: string, description: string) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || "");

  const handleSave = () => {
    if (!title.trim() || saving) return;
    onSave(title.trim(), description.trim());
  };

  return (
    <div className="py-2 px-3 rounded-md border border-primary/30 bg-accent/30 space-y-2">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="text-base md:text-sm h-8"
        maxLength={500}
        autoFocus
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSave();
          if (e.key === "Escape" && !saving) onCancel();
        }}
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Kuvaus (valinnainen)"
        className="text-base md:text-sm min-h-[50px] resize-none"
        rows={2}
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !saving) onCancel();
        }}
      />
      <div className="flex items-center gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving} className="h-7 px-2">
          <CloseIcon className="h-3.5 w-3.5 mr-1" />
          Peruuta
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!title.trim() || saving} className="h-7 px-2">
          {saving ? <SpinnerIcon className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckmarkIcon className="h-3.5 w-3.5 mr-1" />}
          Tallenna
        </Button>
      </div>
    </div>
  );
}

function NewMemoTaskForm({
  onSave,
  saving,
}: {
  onSave: (title: string, description: string, dueDate: string | null) => Promise<boolean>;
  saving: boolean;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState<string | null>(null);

  const handleSave = async () => {
    if (!title.trim() || saving) return;
    const ok = await onSave(title.trim(), description.trim(), dueDate);
    if (ok) {
      setTitle("");
      setDescription("");
      setDueDate(null);
    }
  };

  return (
    <div className="py-2 px-3 rounded-md border border-dashed border-primary/30 bg-accent/20 space-y-2">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Tehtävä"
        className="text-base md:text-sm h-8"
        maxLength={500}
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === "Enter") void handleSave();
        }}
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Kuvaus (valinnainen)"
        className="text-base md:text-sm min-h-[50px] resize-none"
        rows={2}
        disabled={saving}
      />
      <div className="flex items-center gap-2 justify-between">
        <MemoDeadlinePopover
          value={dueDate}
          onChange={setDueDate}
          triggerClassName="text-sm text-muted-foreground"
          trigger={
            <>
              <DateIcon className="h-3.5 w-3.5 shrink-0" />
              <span>{dueDate ? formatDateFi(dueDate) : "Ei määräaikaa"}</span>
            </>
          }
        />
        <Button size="sm" onClick={() => void handleSave()} disabled={!title.trim() || saving} className="h-7 px-2">
          {saving ? <SpinnerIcon className="h-3.5 w-3.5 mr-1 animate-spin" /> : <AddIcon className="h-3.5 w-3.5 mr-1" />}
          Lisää tehtävä
        </Button>
      </div>
    </div>
  );
}

/**
 * Opened view of one user's Muistilista in the `TaskDialog` shell: määräaika
 * filter chips, one row per tehtävä and an add form at the bottom. Every
 * successful write refetches the list and calls `onChanged` so the board
 * card counts follow.
 */
export function MemoListDialog({
  ownerUserId,
  ownerName,
  isOwn,
  open,
  onOpenChange,
  onChanged,
}: {
  ownerUserId: string;
  ownerName: string;
  isOwn: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}) {
  const [chip, setChip] = useState<MemoDeadlineChip>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<MemoTask | null>(null);
  const { tasks, loading, refetch } = useMemoTasks(open ? ownerUserId : null);

  const handleChanged = useCallback(() => {
    void refetch();
    onChanged();
  }, [refetch, onChanged]);
  const { createMemoTask, updateMemoTask, deleteMemoTask } = useMemoTaskMutations(handleChanged);

  const visibleTasks = useMemo(() => filterMemoTasks(tasks, chip, new Date()), [tasks, chip]);
  const title = isOwn ? "Muistilista" : `Muistilista – ${ownerName}`;

  const handleCreate = async (newTitle: string, description: string, dueDate: string | null) => {
    setSaving(true);
    try {
      const result = await createMemoTask({
        ownerUserId,
        title: newTitle,
        description: description || null,
        dueDate,
      });
      return result.success;
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (task: MemoTask, newTitle: string, description: string) => {
    setSaving(true);
    try {
      const result = await updateMemoTask({
        id: task.id,
        version: task.version,
        title: newTitle,
        description,
      });
      if (result.success) setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;
    const result = await deleteMemoTask(taskToDelete.id, taskToDelete.version);
    if (result.success) setTaskToDelete(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-dvh pb-0 max-w-full max-h-dvh rounded-none overflow-hidden flex flex-col border-0 shadow-xl [&>button]:h-8 [&>button]:w-8 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:text-white [&>button]:opacity-80 [&>button]:rounded-md [&>button:hover]:opacity-100 [&>button:hover]:bg-white/15 md:w-full md:h-[min(720px,90dvh)] md:max-w-3xl md:max-h-full md:rounded-lg">
        <DialogHeader className="bg-primary text-primary-foreground md:rounded-t-lg text-left space-y-3">
          <DialogTitle asChild>
            <h2 className="text-lg md:text-2xl font-semibold tracking-tight leading-tight text-primary-foreground break-words pr-12">
              {title}
            </h2>
          </DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0 -mx-6">
          <div className="flex flex-wrap gap-2 px-6 pt-4 pb-3">
            {MEMO_DEADLINE_CHIPS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setChip(c.value)}
                className={cn(
                  "rounded-full border px-3 py-1 text-sm transition-colors",
                  chip === c.value
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
                )}
              >
                {c.label}
              </button>
            ))}
          </div>

          <ScrollArea className="flex-1 px-6">
            <div className="space-y-3 pb-4 pr-4">
              {loading && tasks.length === 0 ? (
                <>
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </>
              ) : visibleTasks.length === 0 ? (
                <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                  Ei tehtäviä
                </div>
              ) : (
                visibleTasks.map((task) =>
                  editingId === task.id ? (
                    <EditableMemoTaskRow
                      key={task.id}
                      task={task}
                      onSave={(t, d) => void handleEdit(task, t, d)}
                      onCancel={() => setEditingId(null)}
                      saving={saving}
                    />
                  ) : (
                    <MemoTaskRow
                      key={task.id}
                      task={task}
                      onUpdate={updateMemoTask}
                      onDelete={setTaskToDelete}
                      onEdit={(t) => setEditingId(t.id)}
                    />
                  ),
                )
              )}
            </div>
          </ScrollArea>

          <div className="border-t px-6 pt-3 pb-6">
            <NewMemoTaskForm onSave={handleCreate} saving={saving} />
          </div>
        </div>
      </DialogContent>
      <AlertDialog
        open={taskToDelete !== null}
        onOpenChange={(next) => {
          if (!next) setTaskToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Poista tehtävä</AlertDialogTitle>
            <AlertDialogDescription>
              Haluatko varmasti poistaa tehtävän "{taskToDelete?.title}"? Tätä toimintoa ei voi perua.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Peruuta</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmDelete();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Poista
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
